/**
 * `IntegrationOAuthCallbackPage` — Task 141 (IC-3 FE wiring).
 *
 * Callback surface for the IC-3 Meta Ads OAuth handshake. Route:
 *   `/auth/integration-oauth-callback`
 *
 * Why a separate page from `/auth/social-oauth-callback`:
 *   IC-1 §2 keeps the publishing OAuth surface untouched. IC-3 builds
 *   a parallel OAuth surface on the integration-credential layer with
 *   different wire markers, a different success-body shape, a
 *   different picker UX (ad-account candidates vs publish-identity
 *   tuples), and a different selection field (`selected_subject_id`
 *   vs `selected_publish_identity_key`). Two separate pages keep the
 *   two flows from tangling.
 *
 * Authority chain (in order):
 *   1. Read `state` + `code` (or `error`) from the URL via
 *      `useSearchParams()`.
 *   2. Validate that `state` is present and exactly one of
 *      `{code, error}` is present.
 *   3. First call: POST `complete-integration-connection-oauth
 *      { state, code }`.
 *   4. Success body (auto-finalize / single candidate): render the
 *      Arabic success banner, navigate to `connection.redirect_back_to`
 *      (or the sessionStorage fallback, or `/clients`) after ~1.2s.
 *   5. Multi-candidate error: parse `fields.candidates[0]` as a JSON
 *      array of `IntegrationAdAccountCandidate`, render the picker.
 *   6. User selects → POST `complete-integration-connection-oauth
 *      { state, selected_subject_id }` → same success branch as step 4.
 *   7. Typed error markers (e.g. `oauth_state_consumed`,
 *      `no_subjects_granted`) → Arabic operator copy.
 *
 * Hard rules:
 *   - NEVER call Meta directly. Meta is the page the user came FROM;
 *     this page only POSTs to the IC-3 EF.
 *   - NEVER touch `ad_accounts` (IC-4 owns it).
 *   - NEVER touch `social_connections` or the publishing OAuth EFs
 *     (IC-1 §2).
 *   - NEVER log the `state` token or `code` (CSRF / auth-grant secrets).
 *   - NEVER request any platform-mutation scope (IC-1 §12 — the EF
 *     enforces this on `scope=ads_read`-only; the FE has no say).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isApiError, KmiApiError } from '../../api/client';
import {
  completeIntegrationConnectionOAuthFirstCall,
  completeIntegrationConnectionOAuthSelect,
  MULTIPLE_SUBJECTS_MARKER,
} from '../../api/endpoints';
import type {
  CompleteIntegrationConnectionOAuthData,
  IntegrationAdAccountCandidate,
  IntegrationConnectionOAuthProvider,
} from '../../api/types';
import {
  IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX,
  IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX,
} from '../client/PaidMediaConnectMetaAdsCard';

type CallbackState =
  | { readonly status: 'starting' }
  | { readonly status: 'success'; readonly data: CompleteIntegrationConnectionOAuthData }
  | {
      readonly status: 'picker';
      readonly candidates: ReadonlyArray<IntegrationAdAccountCandidate>;
      readonly state: string;
      readonly selectedSubjectId: string | null;
      readonly submitting: boolean;
      readonly errorAr: string | null;
    }
  | { readonly status: 'error'; readonly messageAr: string; readonly retryHref: string };

const POST_SUCCESS_REDIRECT_DELAY_MS = 1200;
const GENERIC_OAUTH_ERROR_AR = 'تعذّر إكمال عملية الربط. حاول مرة أخرى بعد قليل.';

/**
 * Task 191 — the integration callback page is SHARED across providers
 * (Meta IC-3, Snapchat IC-6). These helpers derive provider-aware copy so
 * a Snapchat callback never renders hardcoded "Meta" text (and vice
 * versa). The provider comes from the sessionStorage stash the connect
 * card wrote at start (available immediately) and is upgraded to the
 * authoritative `connection.provider` once the success body arrives.
 */
export function providerLabelEn(provider: IntegrationConnectionOAuthProvider | null): string {
  switch (provider) {
    case 'meta':
      return 'Meta';
    case 'snapchat':
      return 'Snapchat';
    case 'tiktok':
      return 'TikTok';
    default:
      return '';
  }
}

/** Provider-aware page heading. Generic when the provider is unknown. */
export function callbackHeadingAr(provider: IntegrationConnectionOAuthProvider | null): string {
  const label = providerLabelEn(provider);
  return label ? `إكمال ربط حساب إعلانات ${label}` : 'إكمال ربط حساب الإعلانات';
}

/** Provider-aware multi-account picker prompt. */
export function pickerPromptAr(provider: IntegrationConnectionOAuthProvider | null): string {
  const label = providerLabelEn(provider);
  const acct = label ? ` في حساب ${label} الخاص بك` : '';
  return `تم العثور على أكثر من حساب إعلاني صالح${acct}. اختر حساب الإعلانات الذي تريد ربطه من القائمة التالية:`;
}

/**
 * Peek the sessionStorage provider stash for a state (set by the connect
 * card at start). Does NOT remove it — `consumeStashedRedirect` cleans up
 * both the redirect and provider keys together. Returns the provider or
 * null.
 */
function peekStashedProvider(state: string): IntegrationConnectionOAuthProvider | null {
  if (typeof window === 'undefined' || !state) return null;
  try {
    const raw = window.sessionStorage.getItem(`${IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX}${state}`);
    if (raw === 'meta' || raw === 'snapchat' || raw === 'tiktok') return raw;
  } catch {
    /* tolerable in privacy-mode contexts */
  }
  return null;
}

/**
 * Validate a redirect path. ONLY in-app paths starting with a single
 * `/` (and not `//`) are accepted; protocol-relative URLs, absolute
 * URLs, and `javascript:` style URIs are rejected. Mirrors the
 * publishing callback's `isSafeRedirectPath`.
 */
function isSafeRedirectPath(path: string | null | undefined): path is string {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (path.length > 1024) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.startsWith('/\\')) return false;
  return true;
}

/**
 * Read + consume the sessionStorage `redirect_back_to` stash for a
 * given state. The IC-3 connect card writes to this key on start;
 * we read it as a fallback when the success body's
 * `connection.redirect_back_to` is null OR when an error path needs
 * to bounce the user somewhere sensible.
 */
function consumeStashedRedirect(state: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // Task 191 — clean up the sibling provider stash whenever we consume
    // the redirect stash (the provider was already read into state at
    // mount; this just avoids leaving the key behind).
    window.sessionStorage.removeItem(`${IC_OAUTH_PROVIDER_SESSION_KEY_PREFIX}${state}`);
    const key = `${IC3_REDIRECT_BACK_TO_SESSION_KEY_PREFIX}${state}`;
    const raw = window.sessionStorage.getItem(key);
    if (raw) {
      window.sessionStorage.removeItem(key);
      if (isSafeRedirectPath(raw)) return raw;
    }
  } catch {
    /* tolerable in privacy-mode contexts */
  }
  return null;
}

/**
 * Pick the navigation target on success. Prefers the server-echoed
 * `connection.redirect_back_to`; falls back to the FE sessionStorage
 * stash; falls back to `/clients` (the agency landing).
 */
function pickPostSuccessTarget(serverRedirect: string | null, stashed: string | null): string {
  if (isSafeRedirectPath(serverRedirect)) return serverRedirect;
  if (isSafeRedirectPath(stashed)) return stashed;
  return '/clients';
}

/**
 * Map a typed wire-error code or `_body` marker to Arabic operator
 * copy. Unknown markers fall back to the generic "try again" message.
 * Marker set comes from the IC-3 callback EF's authority chain (see
 * `tasks/139-ic-3-paid-media-oauth-edge-functions.md`).
 */
export function describeCallbackOAuthError(
  code: string,
  provider: IntegrationConnectionOAuthProvider | null = null,
): string {
  const label = providerLabelEn(provider);
  // Generic fallbacks keep the copy correct when the provider is unknown
  // (e.g. a hand-crafted callback URL with no sessionStorage stash).
  const providerWord = label || 'المزوّد';
  const accountWord = label || 'الإعلانات';
  switch (code) {
    case 'oauth_provider_error':
      return `رفض ${providerWord} إكمال الربط. إذا ألغيت الموافقة من شاشة ${providerWord}، أعد المحاولة وأكمل الموافقة.`;
    case 'oauth_state_invalid':
      return 'رابط الإكمال غير صالح أو منتهي الصلاحية. ابدأ عملية الربط من جديد.';
    case 'oauth_state_consumed':
      return 'تم إكمال هذه العملية مسبقاً. إذا لم يكتمل الربط من جانبك، ابدأ من جديد.';
    case 'oauth_state_expired':
      return 'انتهت صلاحية رابط الإكمال (10 دقائق). ابدأ عملية الربط من جديد.';
    case 'no_subjects_granted':
      return `لم يمنحنا حساب ${accountWord} الخاص بك صلاحية الوصول إلى أي حساب إعلاني. أعد المحاولة وتأكّد من اختيار حساب إعلاني واحد على الأقل أثناء الموافقة.`;
    case 'pending_selection_not_found':
      return 'لم نجد عملية اختيار قيد التنفيذ لهذا الرابط. ابدأ من جديد.';
    case 'pending_selection_already_finalized':
      return 'تم إنهاء هذا الاختيار مسبقاً. ابدأ عملية ربط جديدة إن أردت ربط حساب آخر.';
    case 'pending_selection_expired':
      return 'انتهت صلاحية الاختيار (10 دقائق). ابدأ عملية الربط من جديد.';
    case 'pending_selection_invalid_or_expired':
      return 'الاختيار غير صالح أو منتهي الصلاحية. ابدأ عملية الربط من جديد.';
    case 'selected_subject_not_in_candidates':
      return 'الحساب المختار غير ضمن قائمة الحسابات المتاحة. اختر من القائمة وأعد المحاولة.';
    case 'mode_ambiguous':
    case 'mode_required':
      return 'رابط الإكمال غير صالح. ابدأ عملية الربط من جديد.';
    case 'agency_oauth_path_forbidden':
      return 'هذا الإجراء متاح فقط لمستخدمي العميل (وليس مستخدمي الوكالة).';
    case 'unauthenticated':
      return 'انتهت جلستك. سجّل دخولك من جديد ثم ابدأ عملية الربط.';
    case 'permission_denied':
      return 'لا تملك صلاحية إكمال هذه العملية.';
    case 'not_found':
      return 'العملية غير موجودة أو لم تعد متاحة.';
    case 'internal_error':
    case 'network_error':
    default:
      return GENERIC_OAUTH_ERROR_AR;
  }
}

/**
 * Detect the multi-candidate picker error AND parse the candidate
 * array. Returns the candidates on match, or `null` otherwise so the
 * caller falls through to the generic error mapper.
 */
export function detectMultipleSubjectsPicker(
  err: unknown,
): ReadonlyArray<IntegrationAdAccountCandidate> | null {
  if (!(err instanceof KmiApiError)) return null;
  const fields = err.body?.fields;
  if (!fields || typeof fields !== 'object') return null;
  const body = (fields as Record<string, ReadonlyArray<string>>)['_body'];
  const marker = Array.isArray(body) && typeof body[0] === 'string' ? body[0] : null;
  if (marker !== MULTIPLE_SUBJECTS_MARKER) return null;
  const candidatesJson =
    (fields as Record<string, ReadonlyArray<string>>)['candidates']?.[0] ?? null;
  if (!candidatesJson) return null;
  try {
    const parsed = JSON.parse(candidatesJson) as unknown;
    if (!Array.isArray(parsed)) return null;
    const out: IntegrationAdAccountCandidate[] = [];
    for (const c of parsed) {
      if (!c || typeof c !== 'object') continue;
      const obj = c as Record<string, unknown>;
      if (typeof obj['subject_id'] !== 'string' || (obj['subject_id'] as string).length === 0)
        continue;
      out.push({
        subject_id: obj['subject_id'] as string,
        account_external_id:
          typeof obj['account_external_id'] === 'string'
            ? (obj['account_external_id'] as string)
            : '',
        display_name:
          typeof obj['display_name'] === 'string' ? (obj['display_name'] as string) : '',
        account_status:
          typeof obj['account_status'] === 'number' ? (obj['account_status'] as number) : null,
        currency: typeof obj['currency'] === 'string' ? (obj['currency'] as string) : null,
        timezone_name:
          typeof obj['timezone_name'] === 'string' ? (obj['timezone_name'] as string) : null,
        business:
          obj['business'] &&
          typeof obj['business'] === 'object' &&
          typeof (obj['business'] as Record<string, unknown>)['id'] === 'string' &&
          typeof (obj['business'] as Record<string, unknown>)['name'] === 'string'
            ? {
                id: (obj['business'] as Record<string, unknown>)['id'] as string,
                name: (obj['business'] as Record<string, unknown>)['name'] as string,
              }
            : null,
      });
    }
    return out;
  } catch {
    return null;
  }
}

function describeError(err: unknown, provider: IntegrationConnectionOAuthProvider | null): string {
  if (isApiError(err)) {
    const fieldMarker = readFirstFieldMarker(err.body.fields);
    return describeCallbackOAuthError(fieldMarker ?? err.code, provider);
  }
  return GENERIC_OAUTH_ERROR_AR;
}

function readFirstFieldMarker(
  fields: Readonly<Record<string, ReadonlyArray<string>>> | undefined,
): string | undefined {
  if (!fields) return undefined;
  const bodyMarkers = fields['_body'];
  if (bodyMarkers && bodyMarkers.length > 0) return bodyMarkers[0];
  return undefined;
}

export function IntegrationOAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Task 191 — provider for the shared callback copy. Seeded from the
  // sessionStorage stash the connect card wrote at start (so the heading
  // is correct during 'starting'), then upgraded to the authoritative
  // `connection.provider` once the success body arrives.
  const [provider, setProvider] = useState<IntegrationConnectionOAuthProvider | null>(() =>
    peekStashedProvider(searchParams.get('state') ?? ''),
  );
  const [state, setState] = useState<CallbackState>({ status: 'starting' });

  useEffect(() => {
    const stateParam = searchParams.get('state') ?? '';
    // Task 204 (IC-8) — TikTok returns the grant as `auth_code` (not the
    // OAuth-standard `code` that Meta/Snapchat use). Accept either so the
    // shared callback page completes the TikTok handshake too; the EF
    // receives it under the existing `code` field.
    const codeParam = searchParams.get('code') ?? searchParams.get('auth_code') ?? '';
    const errorParam = searchParams.get('error');
    // Task 191 — read the provider hint once (local, no effect dep) so the
    // error/copy paths below stay provider-aware without re-running the
    // effect when the success branch upgrades `provider` state.
    const stashedProvider = peekStashedProvider(stateParam);

    if (!stateParam) {
      setState({
        status: 'error',
        messageAr: 'رابط غير صالح: حقل state مفقود.',
        retryHref: '/clients',
      });
      return;
    }
    if (errorParam) {
      setState({
        status: 'error',
        messageAr: describeCallbackOAuthError('oauth_provider_error', stashedProvider),
        retryHref: consumeStashedRedirect(stateParam) ?? '/clients',
      });
      return;
    }
    if (!codeParam) {
      setState({
        status: 'error',
        messageAr: 'رابط غير صالح: حقل code مفقود.',
        retryHref: consumeStashedRedirect(stateParam) ?? '/clients',
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await completeIntegrationConnectionOAuthFirstCall({
          input: { state: stateParam, code: codeParam },
        });
        if (cancelled) return;
        const stashed = consumeStashedRedirect(stateParam);
        // Upgrade to the authoritative provider from the success body.
        setProvider(data.connection.provider);
        setState({ status: 'success', data });
        setTimeout(() => {
          if (cancelled) return;
          navigate(pickPostSuccessTarget(data.connection.redirect_back_to, stashed));
        }, POST_SUCCESS_REDIRECT_DELAY_MS);
      } catch (err: unknown) {
        if (cancelled) return;
        const pickerCandidates = detectMultipleSubjectsPicker(err);
        if (pickerCandidates) {
          setState({
            status: 'picker',
            candidates: pickerCandidates,
            state: stateParam,
            selectedSubjectId: null,
            submitting: false,
            errorAr: null,
          });
          return;
        }
        setState({
          status: 'error',
          messageAr: describeError(err, stashedProvider),
          retryHref: consumeStashedRedirect(stateParam) ?? '/clients',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  const handlePickerSubmit = useCallback(
    async (selectedSubjectId: string, pickerState: string) => {
      setState((prev) =>
        prev.status === 'picker' ? { ...prev, submitting: true, errorAr: null } : prev,
      );
      try {
        const data = await completeIntegrationConnectionOAuthSelect({
          input: { state: pickerState, selected_subject_id: selectedSubjectId },
        });
        const stashed = consumeStashedRedirect(pickerState);
        setProvider(data.connection.provider);
        setState({ status: 'success', data });
        setTimeout(() => {
          navigate(pickPostSuccessTarget(data.connection.redirect_back_to, stashed));
        }, POST_SUCCESS_REDIRECT_DELAY_MS);
      } catch (err: unknown) {
        const messageAr = describeError(err, provider);
        setState((prev) =>
          prev.status === 'picker' ? { ...prev, submitting: false, errorAr: messageAr } : prev,
        );
      }
    },
    [navigate, provider],
  );

  return (
    <div className="stack-md" style={{ maxWidth: 720, margin: '40px auto', padding: 16 }}>
      <div className="card" style={{ padding: 22 }}>
        <h2 style={{ marginTop: 0 }}>{callbackHeadingAr(provider)}</h2>

        {state.status === 'starting' ? <p>جارٍ إكمال الربط… الرجاء عدم إغلاق النافذة.</p> : null}

        {state.status === 'success' ? (
          <>
            <p>
              <span className="pill pill-active">تم الربط</span>{' '}
              <strong>
                {state.data.connection.display_name ?? state.data.connection.provider_subject_id}
              </strong>
            </p>
            <p style={{ color: 'var(--muted)' }}>سيتم إعادة توجيهك خلال لحظات…</p>
          </>
        ) : null}

        {state.status === 'picker' ? (
          <MetaAdsPickerView
            promptAr={pickerPromptAr(provider)}
            candidates={state.candidates}
            selectedSubjectId={state.selectedSubjectId}
            submitting={state.submitting}
            errorAr={state.errorAr}
            onSelect={(subjectId) =>
              setState((prev) =>
                prev.status === 'picker' ? { ...prev, selectedSubjectId: subjectId } : prev,
              )
            }
            onSubmit={(subjectId) => handlePickerSubmit(subjectId, state.state)}
          />
        ) : null}

        {state.status === 'error' ? (
          <>
            <div className="banner banner-error" role="alert" style={{ marginBottom: 12 }}>
              {state.messageAr}
            </div>
            <a className="btn-text" href={state.retryHref}>
              العودة وإعادة المحاولة
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface MetaAdsPickerViewProps {
  readonly promptAr: string;
  readonly candidates: ReadonlyArray<IntegrationAdAccountCandidate>;
  readonly selectedSubjectId: string | null;
  readonly submitting: boolean;
  readonly errorAr: string | null;
  readonly onSelect: (subjectId: string) => void;
  readonly onSubmit: (subjectId: string) => void;
}

function MetaAdsPickerView({
  promptAr,
  candidates,
  selectedSubjectId,
  submitting,
  errorAr,
  onSelect,
  onSubmit,
}: MetaAdsPickerViewProps) {
  return (
    <>
      <p>{promptAr}</p>
      <div
        role="radiogroup"
        aria-label={promptAr}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBlock: 12 }}
      >
        {candidates.map((c) => {
          const checked = selectedSubjectId === c.subject_id;
          return (
            <label
              key={c.subject_id}
              className={checked ? 'row-card row-card-active' : 'row-card'}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}
            >
              <input
                type="radio"
                name="meta-ads-candidate"
                value={c.subject_id}
                checked={checked}
                onChange={() => onSelect(c.subject_id)}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <strong>{c.display_name || c.subject_id}</strong>
                  <span
                    className="meta"
                    style={{ direction: 'ltr', unicodeBidi: 'isolate', fontSize: 12 }}
                  >
                    {c.subject_id}
                  </span>
                </div>
                <div className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
                  {c.business ? <>المدير التجاري: {c.business.name} · </> : null}
                  {c.currency ? <>العملة: {c.currency} · </> : null}
                  {c.timezone_name ? <>المنطقة الزمنية: {c.timezone_name}</> : null}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      {errorAr ? (
        <div className="banner banner-error" role="alert" style={{ marginBottom: 12 }}>
          {errorAr}
        </div>
      ) : null}
      <button
        type="button"
        className="btn-primary"
        disabled={!selectedSubjectId || submitting}
        onClick={() => {
          if (selectedSubjectId) onSubmit(selectedSubjectId);
        }}
      >
        {submitting ? 'جاري الربط…' : 'ربط الحساب المختار'}
      </button>
    </>
  );
}
